import "../index.css";
import "./Games.css";
import Footer from "../components/Footer";
import { PageNav } from "../components/PageNav";
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import ApiCall from "../auth/ApiCall";
import { useAdaptivePolling } from "../hooks/useAdaptivePolling";
import { useNavigate } from "react-router-dom";
import { AccessVerification, PremiumAccessCheck } from "../auth/UserProfile";

// ── Interfaces ──────────────────────────────────────────────────────────────

interface ArbLeg {
  selection: string;
  line: number;
  deep_link: string;
  odds_id: string;
  market_id: string;
  // sportsbook field name TBD from console log
  sportsbook?: string;
  book_name?: string;
  book?: string;
  price?: number;
  stake_percent?: number;
  [key: string]: unknown;
}

interface ArbItem {
  id: string;
  away_team: string;
  home_team: string;
  sport_name: string;
  sport_id: number;
  market_label: string;
  market_type: string;
  line: number;
  profit_percent: number;
  estimated_net_profit_percent: number;
  ev_percentage: number | null;
  ev_available: boolean;
  implied_total: number;
  is_live: boolean;
  is_alternate_line: boolean;
  is_player_prop: boolean;
  player_name: string | null;
  possibly_stale: boolean;
  event_start_time: string;
  detected_at: string;
  oldest_odds_age_seconds: number;
  legs: ArbLeg[];
  warnings: string[];
}

interface EvItem {
  id: string;
  arb_available: boolean;
  arb_profit: number;
  away_team: string;
  confidence: number;
  cross_ref_count: number;
  cross_ref_dispersion: number | null;
  detected_at: string;
  display_selection: string;
  est_correction_s: number;
  ev_percentage: number;
  event_id: number;
  event_start_time: string;
  fair_probability: number;
  home_team: string;
  is_alternate_line: boolean;
  is_live: boolean;
  is_player_prop: boolean;
  kelly_percent: number;
  line: number;
  market_type: string;
  market_width: number;
  no_vig_odds: number;
  odds_american: number;
  oldest_odds_age_seconds: number;
  partner_fair_probability: number;
  player_id: number | null;
  player_name: string | null;
  possibly_stale: boolean;
  quality_tier: string;
  selection: string;
  sharp_book: string;
  single_sharp_period: boolean;
  sport_id: number;
  sport_name: string;
  sportsbook: string;
  sportsbooks: string[];
  start_time: string;
  stat_category: string | null;
  warnings: string[];
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function legBook(leg: ArbLeg): string {
  return (
    leg.sportsbook ??
    leg.book_name ??
    (leg["book"] as string | undefined) ??
    leg.odds_id
  );
}

function fmtOdds(price: number | undefined): string {
  if (price == null) return "—";
  return price > 0 ? `+${price}` : String(price);
}

function fmtPct(v: number, decimals = 1): string {
  return `${(v * 100).toFixed(decimals)}%`;
}

function humanizeMarket(s: string): string {
  return s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function tierColor(tier: string): string {
  switch (tier) {
    case "A":
      return "#4ade80";
    case "B":
      return "#a3e635";
    case "C":
      return "#f59e0b";
    default:
      return "#ef4444";
  }
}

// ── Reusable picker (same pattern as Games.tsx) ───────────────────────────

interface PickerOption {
  label: string;
  value: string;
}

const CustomPicker: React.FC<{
  options: PickerOption[];
  selectedValue: string;
  onValueChange: (v: string) => void;
  placeholder?: string;
  prefix_text: string;
  visible: boolean;
  onClose: () => void;
}> = ({
  options,
  selectedValue,
  onValueChange,
  placeholder,
  prefix_text,
  visible,
  onClose,
}) => {
  const handleSelect = (value: string) => {
    onValueChange(value);
    onClose();
  };

  if (!visible) {
    return (
      <p
        className="font_family_base picker-text"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
        }}
      >
        <span>
          {prefix_text}
          {selectedValue || placeholder}
        </span>
        <span
          style={{
            display: "inline-block",
            width: 6,
            height: 6,
            borderRight: "2px solid #fff",
            borderBottom: "2px solid #fff",
            transform: "rotate(45deg)",
            marginBottom: 3,
            flexShrink: 0,
          }}
        />
      </p>
    );
  }

  return (
    <>
      <p className="picker-text">
        {prefix_text}
        {selectedValue || placeholder}
      </p>
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-list">
            {options.map((item) => (
              <div
                key={item.value}
                className={`font_family_base modal-item ${
                  item.value === selectedValue ? "modal-item-selected" : ""
                }`}
                onClick={() => handleSelect(item.value)}
              >
                {item.label}
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

// ── Sub-components ────────────────────────────────────────────────────────────

function Badge({ color, label }: { color: string; label: string }) {
  return (
    <span
      className="font_family_base"
      style={{
        backgroundColor: color,
        color: "#fff",
        fontSize: 10,
        fontWeight: "bold",
        padding: "2px 6px",
        borderRadius: 4,
        letterSpacing: 0.5,
      }}
    >
      {label}
    </span>
  );
}

function StatChip({
  label,
  value,
  highlight = false,
  tooltip,
  valueColor,
}: {
  label: string;
  value: string;
  highlight?: boolean;
  tooltip?: string;
  valueColor?: string;
}) {
  const infoRef = useRef<HTMLSpanElement>(null);
  const [tooltipPos, setTooltipPos] = useState<{ x: number; y: number } | null>(
    null,
  );

  const handleMouseEnter = () => {
    if (infoRef.current) {
      const rect = infoRef.current.getBoundingClientRect();
      setTooltipPos({ x: rect.left + rect.width / 2, y: rect.top });
    }
  };

  return (
    <div
      className="background_1 border_radius_small"
      style={{
        padding: "4px 10px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        flex: 1,
        minWidth: 72,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 3 }}>
        <p
          className="font_family_base"
          style={{
            color: "#888",
            fontSize: 9,
            margin: 0,
            textTransform: "uppercase",
            letterSpacing: 0.5,
          }}
        >
          {label}
        </p>
        {tooltip && (
          <span
            ref={infoRef}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={() => setTooltipPos(null)}
            style={{
              color: "#60a5fa",
              fontSize: 9,
              cursor: "default",
              userSelect: "none",
              lineHeight: 1,
            }}
          >
            ⓘ
          </span>
        )}
      </div>
      {tooltipPos && tooltip && (
        <div
          style={{
            position: "fixed",
            left: tooltipPos.x,
            top: tooltipPos.y - 6,
            transform: "translateX(-50%) translateY(-100%)",
            backgroundColor: "#111",
            border: "1px solid #444",
            borderRadius: 8,
            padding: "8px 12px",
            width: 220,
            zIndex: 9999,
            pointerEvents: "none",
          }}
        >
          <p
            className="font_family_base"
            style={{ color: "#ccc", fontSize: 12, margin: 0, lineHeight: 1.5 }}
          >
            {tooltip}
          </p>
        </div>
      )}
      <p
        className="font_family_base"
        style={{
          color: valueColor ?? (highlight ? "#4ade80" : "#fff"),
          fontSize: 14,
          fontWeight: "bold",
          margin: 0,
        }}
      >
        {value}
      </p>
    </div>
  );
}

function LegCard({
  leg,
  totalStake,
}: {
  leg: ArbLeg;
  totalStake: number | null;
}) {
  const book = legBook(leg);
  const amount =
    totalStake != null && leg.stake_percent != null
      ? (totalStake * leg.stake_percent) / 100
      : null;

  return (
    <div
      className="background_1 border_radius"
      style={{ padding: "8px 12px", flex: 1, minWidth: 140 }}
    >
      <p
        className="font_family_base"
        style={{
          color: "#4ade80",
          fontSize: 12,
          fontWeight: "bold",
          margin: "0 0 2px",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {book}
      </p>
      <p
        className="font_family_base"
        style={{
          color: "#fff",
          fontSize: 15,
          fontWeight: "bold",
          margin: "0 0 2px",
        }}
      >
        {leg.selection} {leg.line}
      </p>
      {leg.stake_percent != null && (
        <p
          className="font_family_base"
          style={{ color: "#facc15", fontSize: 13, margin: 0 }}
        >
          Stake: {leg.stake_percent.toFixed(1)}%
          {amount != null && (
            <span style={{ color: "#fff", marginLeft: 6 }}>
              (${amount.toFixed(2)})
            </span>
          )}
        </p>
      )}
    </div>
  );
}

function ArbCard({ arb }: { arb: ArbItem }) {
  const [expanded, setExpanded] = useState(true);
  const [stakeInput, setStakeInput] = useState("");
  const totalStake = stakeInput !== "" ? parseFloat(stakeInput) : null;

  const date = new Date(arb.event_start_time);
  const isToday = new Date().toDateString() === date.toDateString();
  const timeStr = date.toLocaleString("en-US", {
    hour: "numeric",
    minute: "numeric",
    hour12: true,
  });
  const dateStr = date.toLocaleString("en-US", {
    day: "numeric",
    month: "short",
  });

  return (
    <div
      style={{
        marginBottom: 10,
        borderColor: "#888",
        borderWidth: 1,
        borderStyle: "solid",
        borderRadius: 20,
        overflow: "hidden",
      }}
      className="background_3"
    >
      <div style={{ padding: 12, maxWidth: 700, margin: "0 auto" }}>
        {/* Header: sport · market · badges */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 6,
            flexWrap: "wrap",
          }}
        >
          <p
            className="font_family_base"
            style={{
              color: "#4ade80",
              fontSize: 12,
              fontWeight: "bold",
              margin: 0,
              whiteSpace: "nowrap",
            }}
          >
            {arb.sport_name}
          </p>
          <p
            className="font_family_base"
            style={{ color: "#ccc", fontSize: 13, flex: 1, margin: 0 }}
          >
            {arb.market_label}
            {arb.line != null ? ` ${arb.line}` : ""}
            {arb.player_name ? ` — ${arb.player_name}` : ""}
          </p>
          <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
            {arb.is_live && <Badge color="#ef4444" label="LIVE" />}
            {arb.is_alternate_line && (
              <Badge color="#f59e0b" label="ALT LINE" />
            )}
            {arb.is_player_prop && <Badge color="#8b5cf6" label="PROP" />}
            {arb.possibly_stale && <Badge color="#6b7280" label="STALE" />}
          </div>
        </div>

        {/* Teams + time */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 10,
            flexWrap: "wrap",
          }}
        >
          <p
            className="font_family_base"
            style={{
              color: "#fff",
              fontSize: 16,
              fontWeight: "bold",
              margin: 0,
              flex: 1,
            }}
          >
            {arb.away_team} @ {arb.home_team}
          </p>
          <p
            className="font_family_base"
            style={{
              color: "#888",
              fontSize: 12,
              margin: 0,
              whiteSpace: "nowrap",
            }}
          >
            {isToday ? `Today ${timeStr}` : `${dateStr} ${timeStr}`}
          </p>
        </div>

        {/* Stats row */}
        <div
          style={{
            display: "flex",
            gap: 8,
            flexWrap: "wrap",
            marginBottom: 10,
          }}
        >
          <StatChip
            label="Profit"
            value={`+${arb.profit_percent.toFixed(2)}%`}
            highlight
          />
          <StatChip
            label="Est. Net"
            value={`+${arb.estimated_net_profit_percent.toFixed(2)}%`}
          />
          <StatChip
            label="Implied"
            value={`${arb.implied_total.toFixed(2)}%`}
          />
          {arb.is_alternate_line && <StatChip label="Alt Line" value="Yes" />}
        </div>

        {/* Legs */}
        <div>
          <button
            onClick={() => setExpanded(!expanded)}
            className="font_family_base"
            style={{
              background: "transparent",
              border: "none",
              color: "#888",
              fontSize: 12,
              cursor: "pointer",
              padding: "0 0 6px",
              display: "flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            {expanded ? "▾" : "▸"} Legs ({arb.legs.length})
          </button>
          {expanded && (
            <>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  marginBottom: 8,
                }}
              >
                <p
                  className="font_family_base"
                  style={{
                    color: "#888",
                    fontSize: 12,
                    margin: 0,
                    whiteSpace: "nowrap",
                  }}
                >
                  Total stake:
                </p>
                <span
                  className="font_family_base"
                  style={{ color: "#ccc", fontSize: 12 }}
                >
                  $
                </span>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={stakeInput}
                  onChange={(e) => setStakeInput(e.target.value)}
                  placeholder="0"
                  className="font_family_base"
                  style={{
                    width: 80,
                    backgroundColor: "#1a1a1a",
                    border: "1px solid #444",
                    borderRadius: 6,
                    color: "#fff",
                    fontSize: 13,
                    padding: "3px 8px",
                  }}
                />
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {arb.legs.map((leg, i) => (
                  <LegCard
                    key={i}
                    leg={leg}
                    totalStake={isNaN(totalStake as number) ? null : totalStake}
                  />
                ))}
              </div>
            </>
          )}
        </div>

        {/* Warnings */}
        {/* {arb.warnings.length > 0 && (
          <p
            className="font_family_base"
            style={{ color: "#f59e0b", fontSize: 11, margin: "8px 0 0" }}
          >
            ⚠ {arb.warnings.join(" • ")}
          </p>
        )} */}
      </div>
    </div>
  );
}

function EvCard({ ev }: { ev: EvItem }) {
  const date = new Date(ev.event_start_time);
  const isToday = new Date().toDateString() === date.toDateString();
  const timeStr = date.toLocaleString("en-US", {
    hour: "numeric",
    minute: "numeric",
    hour12: true,
  });
  const dateStr = date.toLocaleString("en-US", {
    day: "numeric",
    month: "short",
  });

  const tc = tierColor(ev.quality_tier);

  return (
    <div
      style={{
        marginBottom: 10,
        borderColor: "#888",
        borderWidth: 1,
        borderStyle: "solid",
        borderRadius: 20,
        overflow: "hidden",
      }}
      className="background_3"
    >
      <div style={{ padding: 12, maxWidth: 700, margin: "0 auto" }}>
        {/* Header: sport · display_selection · badges */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 6,
            flexWrap: "wrap",
          }}
        >
          <p
            className="font_family_base"
            style={{
              color: "#4ade80",
              fontSize: 12,
              fontWeight: "bold",
              margin: 0,
              whiteSpace: "nowrap",
            }}
          >
            {ev.sport_name}
          </p>
          <p
            className="font_family_base"
            style={{ color: "#ccc", fontSize: 13, flex: 1, margin: 0 }}
          >
            {ev.display_selection}
            {ev.player_name ? ` — ${ev.player_name}` : ""}
            <span style={{ color: "#666", fontSize: 11, marginLeft: 6 }}>
              {humanizeMarket(ev.market_type)}
            </span>
          </p>
          <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
            {ev.is_live && <Badge color="#ef4444" label="LIVE" />}
            {ev.is_alternate_line && <Badge color="#f59e0b" label="ALT LINE" />}
            {ev.is_player_prop && <Badge color="#8b5cf6" label="PROP" />}
            {ev.possibly_stale && <Badge color="#6b7280" label="STALE" />}
          </div>
        </div>

        {/* Teams + time */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 10,
            flexWrap: "wrap",
          }}
        >
          <p
            className="font_family_base"
            style={{
              color: "#fff",
              fontSize: 16,
              fontWeight: "bold",
              margin: 0,
              flex: 1,
            }}
          >
            {ev.away_team} @ {ev.home_team}
          </p>
          <p
            className="font_family_base"
            style={{
              color: "#888",
              fontSize: 12,
              margin: 0,
              whiteSpace: "nowrap",
            }}
          >
            {isToday ? `Today ${timeStr}` : `${dateStr} ${timeStr}`}
          </p>
        </div>

        {/* Primary stats: EV, kelly, confidence, tier */}
        <div
          style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}
        >
          <StatChip
            label="EV"
            value={`+${ev.ev_percentage.toFixed(1)}%`}
            highlight
            tooltip="Expected Value — the estimated edge over the true odds. A higher % means you're getting paid more than the fair probability warrants."
          />
          <StatChip
            label="Kelly"
            value={`${ev.kelly_percent.toFixed(2)}%`}
            tooltip="Kelly Criterion — the recommended fraction of your bankroll to wager, calculated from the EV and win probability. Useful for sizing bets."
          />
          <StatChip
            label="Confidence"
            value={`${ev.confidence}%`}
            tooltip="How reliably this EV estimate is calculated, based on the number and consistency of sharp books pricing this market."
          />
          <StatChip
            label="Tier"
            value={ev.quality_tier}
            valueColor={tc}
            tooltip="Overall quality grade (A–D) combining EV size, confidence, and market conditions. A is the highest quality opportunity."
          />
        </div>

        {/* Secondary stats: odds, probabilities, width */}
        <div
          style={{
            display: "flex",
            gap: 8,
            flexWrap: "wrap",
            marginBottom: 10,
          }}
        >
          <StatChip
            label="Odds"
            value={fmtOdds(ev.odds_american)}
            tooltip="American odds offered by the sportsbook for this bet."
          />
          <StatChip
            label="No-Vig"
            value={fmtOdds(ev.no_vig_odds)}
            tooltip="The fair odds after removing the sportsbook's margin (vig), derived from sharp market prices."
          />
          <StatChip
            label="Fair Prob"
            value={fmtPct(ev.fair_probability)}
            tooltip="The estimated true probability of this outcome, based on sharp market consensus prices after removing the vig."
          />
          <StatChip
            label="Mkt Width"
            value={ev.market_width.toFixed(2)}
            tooltip="The spread between the sharpest available odds across books — a tighter width signals a more liquid, reliable market."
          />
        </div>

        {/* Book + selection */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <p
            className="font_family_base"
            style={{
              color: "#4ade80",
              fontSize: 13,
              fontWeight: "bold",
              margin: 0,
            }}
          >
            {ev.sportsbook}
          </p>
          <p
            className="font_family_base"
            style={{ color: "#aaa", fontSize: 12, margin: 0 }}
          >
            {ev.selection} {ev.line}
          </p>
          {ev.sportsbooks.length > 1 && (
            <p
              className="font_family_base"
              style={{ color: "#666", fontSize: 11, margin: 0 }}
            >
              +{ev.sportsbooks.length - 1} more book
              {ev.sportsbooks.length > 2 ? "s" : ""}
            </p>
          )}
        </div>

        {/* Warnings */}
        {/* {ev.warnings.length > 0 && (
          <p
            className="font_family_base"
            style={{ color: "#f59e0b", fontSize: 11, margin: "8px 0 0" }}
          >
            ⚠ {ev.warnings.join(" • ")}
          </p>
        )} */}
      </div>
    </div>
  );
}

// ── Range filter input ────────────────────────────────────────────────────────

function RangeFilter({
  label,
  minVal,
  maxVal,
  onMinChange,
  onMaxChange,
  suffix,
  step = "any",
}: {
  label: string;
  minVal: string;
  maxVal: string;
  onMinChange: (v: string) => void;
  onMaxChange: (v: string) => void;
  suffix?: string;
  step?: string;
}) {
  const inputStyle: React.CSSProperties = {
    width: 72,
    backgroundColor: "#1a1a1a",
    border: "1px solid #444",
    borderRadius: 6,
    color: "#fff",
    fontSize: 13,
    padding: "5px 8px",
    textAlign: "center",
  };
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <span
        className="font_family_base"
        style={{ color: "#FFF", fontSize: 12, whiteSpace: "nowrap" }}
      >
        {label}:
      </span>
      <input
        type="number"
        value={minVal}
        step={step}
        onChange={(e) => onMinChange(e.target.value)}
        placeholder="Min"
        className="font_family_base"
        style={inputStyle}
      />
      <span
        className="font_family_base"
        style={{ color: "#555", fontSize: 12 }}
      >
        –
      </span>
      <input
        type="number"
        value={maxVal}
        step={step}
        onChange={(e) => onMaxChange(e.target.value)}
        placeholder="Max"
        className="font_family_base"
        style={inputStyle}
      />
      {suffix && (
        <span
          className="font_family_base"
          style={{ color: "#FFF", fontSize: 12 }}
        >
          {suffix}
        </span>
      )}
    </div>
  );
}

// ── Filter constants ──────────────────────────────────────────────────────────

type TypeFilter = "arb" | "ev";

// ── Main page ─────────────────────────────────────────────────────────────────

export default function AdvantageBetting() {
  const navigate = useNavigate();
  const [rawData, setRawData] = useState<{
    arbitrage: ArbItem[];
    ev: EvItem[];
  } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [selectedSport, setSelectedSport] = useState("ALL");
  const [liveFilter, setLiveFilter] = useState("upcoming");
  const [typeFilter, setTypeFilter] = useState<TypeFilter>("arb");

  // EV-specific range filters
  const [evMin, setEvMin] = useState("");
  const [evMax, setEvMax] = useState("");
  const [noVigMin, setNoVigMin] = useState("");
  const [noVigMax, setNoVigMax] = useState("");

  // Dropdown options populated from API metadata
  const [sportOptions, setSportOptions] = useState<PickerOption[]>([
    { label: "ALL", value: "ALL" },
  ]);

  // Modal visibility
  const [sportModalVisible, setSportModalVisible] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const redirectPath = AccessVerification() || PremiumAccessCheck();
    if (redirectPath) navigate(redirectPath);
  }, [navigate]);

  const fetchData = useCallback(async (signal: AbortSignal) => {
    try {
      const response = await ApiCall.makeAuthenticatedRequest(
        "/api/active_opportunities",
        { signal },
      );
      if (signal.aborted) return;
      if (response.error) {
        setError(response.error as string);
      } else {
        const responseData = response as Record<string, unknown>;
        const typed = response as { arbitrage: ArbItem[]; ev: EvItem[] };

        // Parse sports from sportsMetadata (same pattern as Games.tsx)
        const sportsMetadataObj = responseData["sportsMetadata"] as Record<
          string,
          unknown
        >;
        if (sportsMetadataObj) {
          const sportNames: string[] = [];
          for (const key in sportsMetadataObj) {
            const name = (sportsMetadataObj[key] as Record<string, unknown>)[
              "name"
            ] as string;
            if (name) sportNames.push(name);
          }
          setSportOptions([
            { label: "ALL", value: "ALL" },
            ...sportNames.sort().map((n) => ({ label: n, value: n })),
          ]);
        }

        setRawData(typed);
        setError(null);
      }
    } catch (err: unknown) {
      if (signal.aborted) return;
      setError((err as Error).message ?? "Failed to load data.");
    } finally {
      if (!signal.aborted) setIsLoading(false);
    }
  }, []);

  // Initial load + auto-refresh (5s, backs off to 15s on a slow connection)
  useAdaptivePolling(fetchData);

  const arbitrage = rawData?.arbitrage ?? [];

  // Filtered arbitrage list
  const filteredArb = useMemo(() => {
    if (typeFilter === "ev") return [];
    return arbitrage.filter((a) => {
      if (selectedSport !== "ALL" && a.sport_name !== selectedSport)
        return false;
      if (liveFilter === "live" && !a.is_live) return false;
      if (liveFilter === "upcoming" && a.is_live) return false;
      return true;
    });
  }, [arbitrage, selectedSport, liveFilter, typeFilter]);

  // Filtered EV list
  const filteredEv = useMemo(() => {
    if (typeFilter === "arb") return [];
    const evMinN = evMin !== "" ? parseFloat(evMin) : null;
    const evMaxN = evMax !== "" ? parseFloat(evMax) : null;
    const noVigMinN = noVigMin !== "" ? parseFloat(noVigMin) : null;
    const noVigMaxN = noVigMax !== "" ? parseFloat(noVigMax) : null;
    return (rawData?.ev ?? []).filter((e) => {
      if (selectedSport !== "ALL" && e.sport_name !== selectedSport)
        return false;
      if (liveFilter === "live" && !e.is_live) return false;
      if (liveFilter === "upcoming" && e.is_live) return false;
      if (evMinN !== null && e.ev_percentage < evMinN) return false;
      if (evMaxN !== null && e.ev_percentage > evMaxN) return false;
      if (noVigMinN !== null && e.no_vig_odds < noVigMinN) return false;
      if (noVigMaxN !== null && e.no_vig_odds > noVigMaxN) return false;
      return true;
    });
  }, [
    rawData,
    selectedSport,
    liveFilter,
    typeFilter,
    evMin,
    evMax,
    noVigMin,
    noVigMax,
  ]);

  const totalCount = filteredArb.length + filteredEv.length;

  const filterRow = (
    <>
      {/* Sport */}
      <div
        className="games-picker-bubble games-checkbox-inactive background_3 border_radius has_border"
        style={{ width: 180, minWidth: 180, maxWidth: 180 }}
        onClick={() => setSportModalVisible(true)}
      >
        <CustomPicker
          options={sportOptions}
          selectedValue={selectedSport}
          onValueChange={setSelectedSport}
          placeholder="Sport"
          prefix_text="Sport: "
          visible={sportModalVisible}
          onClose={() => setSportModalVisible(false)}
        />
      </div>
    </>
  );

  return (
    <div
      style={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}
    >
      <PageNav />
      <div style={{ flex: 1 }}>
        <div className="main_div">
          <h2
            className="font_family_base text_green"
            style={{
              fontSize: 24,
              fontWeight: "bold",
              marginBottom: 12,
              textAlign: "center",
            }}
          >
            Advantage Betting
          </h2>

          {/* ── Desktop: all 4 controls on one row ── */}
          <div className="show-desktop">
            <div className="games-filters" style={{ justifyContent: "center" }}>
              {/* Live slider (180px = 90px × 2) */}
              <div
                className="background_1 border_radius"
                style={{ width: 180, position: "relative", display: "flex" }}
              >
                <div
                  className="background_3 border_radius toggle-slider"
                  style={{
                    position: "absolute",
                    width: "50%",
                    height: "100%",
                    top: 0,
                    border: "2px solid #007bff",
                    boxSizing: "border-box",
                    transform:
                      liveFilter === "live"
                        ? "translateX(0%)"
                        : "translateX(100%)",
                    transition: "transform 0.2s",
                  }}
                />
                {(["live", "upcoming"] as const).map((val) => (
                  <div
                    key={val}
                    className="font_family_base bill-type-button-base border_radius"
                    onClick={() => setLiveFilter(val)}
                    style={{
                      flex: 1,
                      textAlign: "center",
                      position: "relative",
                      zIndex: 1,
                      cursor: "pointer",
                      userSelect: "none",
                    }}
                  >
                    {val === "live" ? "Live" : "Upcoming"}
                  </div>
                ))}
              </div>

              {/* Type slider (180px = 90px × 2) */}
              <div
                className="background_1 border_radius"
                style={{ width: 180, position: "relative", display: "flex" }}
              >
                <div
                  className="background_3 border_radius toggle-slider"
                  style={{
                    position: "absolute",
                    width: "50%",
                    height: "100%",
                    top: 0,
                    border: "2px solid #007bff",
                    boxSizing: "border-box",
                    transform:
                      typeFilter === "arb"
                        ? "translateX(0%)"
                        : "translateX(100%)",
                    transition: "transform 0.2s",
                  }}
                />
                {(["arb", "ev"] as TypeFilter[]).map((val) => (
                  <div
                    key={val}
                    className="font_family_base bill-type-button-base border_radius"
                    onClick={() => setTypeFilter(val)}
                    style={{
                      flex: 1,
                      textAlign: "center",
                      position: "relative",
                      zIndex: 1,
                      cursor: "pointer",
                      userSelect: "none",
                    }}
                  >
                    {val === "arb" ? "Arbitrage" : "EV"}
                  </div>
                ))}
              </div>

              {/* Sport + Book pickers */}
              {filterRow}
            </div>
          </div>

          {/* ── Mobile: stacked sliders then dropdowns ── */}
          <div className="show-mobile">
            {/* Live slider */}
            <div className="center_me">
              <div
                className="border_radius center_me"
                style={{ padding: 5, marginBottom: 10 }}
              >
                <div
                  className="background_1 border_radius"
                  style={{ width: 300, position: "relative", display: "flex" }}
                >
                  <div
                    className="background_3 border_radius toggle-slider"
                    style={{
                      position: "absolute",
                      width: "50%",
                      height: "100%",
                      top: 0,
                      border: "2px solid #007bff",
                      boxSizing: "border-box",
                      transform:
                        liveFilter === "live"
                          ? "translateX(0%)"
                          : "translateX(100%)",
                      transition: "transform 0.2s",
                    }}
                  />
                  {(["live", "upcoming"] as const).map((val) => (
                    <div
                      key={val}
                      className="font_family_base bill-type-button-base border_radius"
                      onClick={() => setLiveFilter(val)}
                      style={{
                        flex: 1,
                        textAlign: "center",
                        position: "relative",
                        zIndex: 1,
                        cursor: "pointer",
                        userSelect: "none",
                      }}
                    >
                      {val === "live" ? "Live" : "Upcoming"}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Type slider */}
            <div className="center_me">
              <div
                className="border_radius center_me"
                style={{ padding: 5, marginBottom: 10 }}
              >
                <div
                  className="background_1 border_radius"
                  style={{ width: 300, position: "relative", display: "flex" }}
                >
                  <div
                    className="background_3 border_radius toggle-slider"
                    style={{
                      position: "absolute",
                      width: "50%",
                      height: "100%",
                      top: 0,
                      border: "2px solid #007bff",
                      boxSizing: "border-box",
                      transform:
                        typeFilter === "arb"
                          ? "translateX(0%)"
                          : "translateX(100%)",
                      transition: "transform 0.2s",
                    }}
                  />
                  {(["arb", "ev"] as TypeFilter[]).map((val) => (
                    <div
                      key={val}
                      className="font_family_base bill-type-button-base border_radius"
                      onClick={() => setTypeFilter(val)}
                      style={{
                        flex: 1,
                        textAlign: "center",
                        position: "relative",
                        zIndex: 1,
                        cursor: "pointer",
                        userSelect: "none",
                      }}
                    >
                      {val === "arb" ? "Arbitrage" : "EV"}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Sport + Book dropdowns */}
            <div className="games-filters" style={{ justifyContent: "center" }}>
              {filterRow}
            </div>
          </div>

          {/* EV-specific range filters */}
          {typeFilter === "ev" && (
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 16,
                justifyContent: "center",
                alignItems: "center",
                padding: "10px 12px 4px",
              }}
            >
              <RangeFilter
                label="EV %"
                minVal={evMin}
                maxVal={evMax}
                onMinChange={setEvMin}
                onMaxChange={setEvMax}
                suffix="%"
                step="0.1"
              />
              <RangeFilter
                label="No-Vig"
                minVal={noVigMin}
                maxVal={noVigMax}
                onMinChange={setNoVigMin}
                onMaxChange={setNoVigMax}
                suffix="odds"
                step="1"
              />
            </div>
          )}

          {/* Result count */}
          {/* {!isLoading && !error && (
            <p
              className="font_family_base"
              style={{
                color: "#666",
                fontSize: 13,
                textAlign: "center",
                margin: "4px 0 0",
              }}
            >
              {totalCount} {totalCount === 1 ? "opportunity" : "opportunities"}
            </p>
          )} */}
        </div>

        {/* Cards */}
        <div
          style={{ padding: "8px 10px 40px", maxWidth: 720, margin: "0 auto" }}
        >
          {isLoading ? (
            <div style={{ textAlign: "center", padding: 40 }}>
              <div
                className="loading-overlay-spinner"
                style={{ margin: "0 auto" }}
              />
            </div>
          ) : error && !rawData ? (
            <p
              className="font_family_base"
              style={{ color: "#f87171", textAlign: "center", padding: 20 }}
            >
              {error}
            </p>
          ) : totalCount === 0 ? (
            <div style={{ textAlign: "center", padding: 40 }}>
              <p
                className="font_family_base"
                style={{ color: "#888", fontSize: 16 }}
              >
                No opportunities found.
              </p>
              <p
                className="font_family_base"
                style={{ color: "#555", fontSize: 13, marginTop: 6 }}
              >
                Try adjusting your filters or check back soon.
              </p>
            </div>
          ) : (
            <>
              {/* Arbitrage section */}
              {filteredArb.length > 0 && (
                <>
                  {filteredArb.map((arb) => (
                    <ArbCard key={arb.id} arb={arb} />
                  ))}
                </>
              )}

              {/* EV section */}
              {filteredEv.length > 0 && (
                <>
                  {filteredEv.map((ev) => (
                    <EvCard key={ev.id} ev={ev} />
                  ))}
                </>
              )}
            </>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
